import { type ChangeEvent, useState } from "react";

import BackIcon from "@mui/icons-material/ArrowBackRounded";
import {
  Box,
  Card,
  CardContent,
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import IconButton from "@mui/material/IconButton";

import { AddArn } from "./AddARN";
import { FeaturesSelection } from "./FeaturesSelection/FeaturesSelection";

enum Mode {
  CloudFormation = "cf",
  ARN = "arn",
}

type LinkAccountProps = {
  backPageCallback: () => void;
};

/**
 * Screen to link new account to CMP
 */
export const LinkAccount = ({ backPageCallback }: LinkAccountProps) => {
  const [mode, setMode] = useState<string>(Mode.CloudFormation);

  const handleModeSelection = (event: ChangeEvent<HTMLInputElement>) => {
    setMode((event.target as HTMLInputElement).value);
  };

  return (
    <>
      <Card sx={{ border: "none" }}>
        <CardContent>
          <Grid container>
            <Grid
              size={{
                md: 3,
              }}
            >
              <Box>
                <IconButton
                  aria-label="Back"
                  data-cy="link-account-back-button"
                  onClick={backPageCallback}
                  size="large"
                >
                  <BackIcon color="primary" />
                </IconButton>
                Back
              </Box>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6,
              }}
            >
              <Box
                sx={{
                  pt: 2,
                  pb: 3,
                }}
              >
                <FormControl component="fieldset">
                  <Box>
                    <Typography data-cy="link-account-header" variant="subtitle1" mb={2}>
                      Select one of the following options to link your AWS account
                    </Typography>
                  </Box>
                  <RadioGroup value={mode} onChange={handleModeSelection}>
                    <FormControlLabel
                      value={Mode.CloudFormation}
                      control={<Radio data-cy="auto-role-radio" color="primary" />}
                      label="Create a role automatically."
                    />
                    <FormControlLabel
                      value={Mode.ARN}
                      control={<Radio data-cy="manual-role-radio" color="primary" />}
                      label="Create a role manually."
                    />
                  </RadioGroup>
                </FormControl>
              </Box>

              <Divider />

              {mode === Mode.CloudFormation && (
                <Box
                  sx={{
                    pt: 4,
                  }}
                >
                  <Typography data-cy="link-account-features-header" variant="subtitle1" mb={2}>
                    Select features you'd like to enable on your account
                  </Typography>

                  <FeaturesSelection onUpdate={backPageCallback} />
                </Box>
              )}

              {mode === Mode.ARN && (
                <Box
                  sx={{
                    pt: 4,
                  }}
                >
                  <AddArn />
                </Box>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  );
};
